import * as React from "react"
import {useEffect} from "react"
import {createPortal} from "react-dom"
import {cn} from "../lib/fns";

// Context for sharing state between Popover components
interface PopoverContextValue {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  triggerRef: React.RefObject<HTMLButtonElement>
  contentRef: React.RefObject<HTMLDivElement>
  onOpenChange?: (open: boolean) => void
}

const PopoverContext = React.createContext<PopoverContextValue | null>(null)

function usePopoverContext() {
  const context = React.useContext(PopoverContext)
  if (!context) {
    throw new Error("Popover components must be used within a Popover")
  }
  return context
}

// Popover Root Component
interface PopoverProps {
  children: React.ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
  defaultOpen?: boolean;
}

function Popover({children, open: controlledOpen, onOpenChange, defaultOpen = false}: PopoverProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen)
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const contentRef = React.useRef<HTMLDivElement>(null)
  
  // Support both controlled and uncontrolled modes
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : uncontrolledOpen
  
  const setOpen = React.useCallback((value: React.SetStateAction<boolean>) => {
    const newValue = typeof value === "function" ? value(open) : value
    if (!isControlled) {
      setUncontrolledOpen(newValue)
    }
    onOpenChange?.(newValue)
  }, [isControlled, open, onOpenChange])
  
  return (<PopoverContext.Provider value={{open, setOpen, triggerRef, contentRef, onOpenChange}}>
      {children}
    </PopoverContext.Provider>)
}

// Popover Trigger Component
interface PopoverTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean
}

function PopoverTrigger({children, asChild, className, onClick, ...props}: PopoverTriggerProps) {
  const {open, setOpen, triggerRef} = usePopoverContext()
  
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setOpen(!open)
    onClick?.(e)
  }
  
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<any>, {
      ref: triggerRef,
      onClick: handleClick,
      "aria-expanded": open,
      "aria-haspopup": "dialog",
      "data-state": open ? "open" : "closed",
    })
  }
  
  return (<button
      ref={triggerRef}
      type="button"
      aria-expanded={open}
      aria-haspopup="dialog"
      data-state={open ? "open" : "closed"}
      className={className}
      onClick={handleClick}
      {...props}
    >
      {children}
    </button>)
}

// Popover Content Component
type Side = "top" | "right" | "bottom" | "left"
type Align = "start" | "center" | "end"

interface PopoverContentProps extends React.HTMLAttributes<HTMLDivElement> {
  side?: Side
  sideOffset?: number
  align?: Align
  alignOffset?: number
  avoidCollisions?: boolean
  isFocusable?: boolean
}

function PopoverContent({
                          children,
                          className,
                          side = "bottom",
                          sideOffset = 4,
                          align = "center",
                          alignOffset = 0,
                          avoidCollisions = true,
                          isFocusable = true,
                          ...props
                        }: PopoverContentProps) {
  const {open, setOpen, triggerRef, contentRef} = usePopoverContext()
  const [position, setPosition] = React.useState({top: 0, left: 0})
  const [actualSide, setActualSide] = React.useState<Side>(side)
  const [mounted, setMounted] = React.useState(false)
  
  // Handle mounting for portal
  useEffect(() => {
    setMounted(true)
    return () => setMounted(false)
  }, [])
  
  // Calculate position
  useEffect(() => {
    if (!open || !triggerRef.current || !contentRef.current) return
    
    const calculatePosition = () => {
      const trigger = triggerRef.current!
      const content = contentRef.current!
      const triggerRect = trigger.getBoundingClientRect()
      const contentRect = content.getBoundingClientRect()
      const viewport = {
        width: window.innerWidth, height: window.innerHeight,
      }
      
      let top = 0
      let left = 0
      let finalSide = side
      
      // Calculate initial position based on side
      const positions = {
        top: {
          top: triggerRect.top - contentRect.height - sideOffset, left: triggerRect.left,
        }, bottom: {
          top: triggerRect.bottom + sideOffset, left: triggerRect.left,
        }, left: {
          top: triggerRect.top, left: triggerRect.left - contentRect.width - sideOffset,
        }, right: {
          top: triggerRect.top, left: triggerRect.right + sideOffset,
        },
      }
      
      top = positions[side].top
      left = positions[side].left
      
      // Adjust alignment
      if (side === "top" || side === "bottom") {
        if (align === "center") {
          left = triggerRect.left + (triggerRect.width - contentRect.width) / 2
        }
        else if (align === "end") {
          left = triggerRect.right - contentRect.width
        }
        left += alignOffset
      }
      else {
        if (align === "center") {
          top = triggerRect.top + (triggerRect.height - contentRect.height) / 2
        }
        else if (align === "end") {
          top = triggerRect.bottom - contentRect.height
        }
        top += alignOffset
      }
      
      // Collision detection and flip
      if (avoidCollisions) {
        // Check vertical overflow
        if (side === "bottom" && top + contentRect.height > viewport.height) {
          top = triggerRect.top - contentRect.height - sideOffset
          finalSide = "top"
        }
        else if (side === "top" && top < 0) {
          top = triggerRect.bottom + sideOffset
          finalSide = "bottom"
        }
        
        // Check horizontal overflow
        if (side === "right" && left + contentRect.width > viewport.width) {
          left = triggerRect.left - contentRect.width - sideOffset
          finalSide = "left"
        }
        else if (side === "left" && left < 0) {
          left = triggerRect.right + sideOffset
          finalSide = "right"
        }
        
        // Prevent going off screen horizontally
        if (left < 8) left = 8
        if (left + contentRect.width > viewport.width - 8) {
          left = viewport.width - contentRect.width - 8
        }
        
        // Prevent going off screen vertically
        if (top < 8) top = 8
        if (top + contentRect.height > viewport.height - 8) {
          top = viewport.height - contentRect.height - 8
        }
      }
      
      setPosition({top, left})
      setActualSide(finalSide)
    }
    
    calculatePosition()
    
    // Recalculate on scroll/resize
    window.addEventListener("scroll", calculatePosition, true)
    window.addEventListener("resize", calculatePosition)
    
    return () => {
      window.removeEventListener("scroll", calculatePosition, true)
      window.removeEventListener("resize", calculatePosition)
    }
  }, [open, side, sideOffset, align, alignOffset, avoidCollisions, triggerRef, contentRef])
  
  // Handle click outside
  useEffect(() => {
    if (!open) return
    
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (contentRef.current && !contentRef.current.contains(target) && triggerRef.current && !triggerRef.current.contains(target)) {
        setOpen(false)
      }
    }
    
    // Handle escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    
    document.addEventListener("pointerdown", handleClickOutside)
    document.addEventListener("keydown", handleEscape)
    
    return () => {
      document.removeEventListener("pointerdown", handleClickOutside)
      document.removeEventListener("keydown", handleEscape)
    }
  }, [open, setOpen, contentRef, triggerRef])
  
  // Focus management
  useEffect(() => {
    if (open && contentRef.current && isFocusable) {
      const focusable = contentRef.current.querySelector<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
      focusable?.focus()
    }
  }, [open, contentRef, isFocusable])
  
  if (!mounted || !open) return null
  
  const slideAnimations = {
    top: "animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-2",
    bottom: "animate-in fade-in-0 zoom-in-95 slide-in-from-top-2",
    left: "animate-in fade-in-0 zoom-in-95 slide-in-from-right-2",
    right: "animate-in fade-in-0 zoom-in-95 slide-in-from-left-2",
  }
  
  return (<>{createPortal(<div
    ref={contentRef}
    role="dialog"
    aria-modal="true"
    data-state={open ? "open" : "closed"}
    data-side={actualSide}
    style={{
      position: "fixed", top: position.top, left: position.left, zIndex: 50,
    }}
    className={cn("outline-none", slideAnimations[actualSide], className)}
    {...props}
  >
    {children}
  </div>, document.body)}</>)
}

export {
  Popover, PopoverTrigger, PopoverContent, type PopoverProps, type PopoverTriggerProps, type PopoverContentProps,
}
